/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Formik } from 'formik';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CircularProgress, Grid, Typography } from '@mui/material';

import { DefaultLanguage } from 'shared';

import { AlignCenter } from '../../../../components/AlignCenter/AlignCenter';
import { HUModal2 } from '../../../../components/HUModal2/HUModal2';
import { SimpleConfirmModal } from '../../../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleNotifyModal } from '../../../../components/SimpleNotifyModal/SimpleNotifyModal';
import { Namespaces } from '../../../../i18n';
import { SuperuserUserApiService } from '../../../../services/SuperuserUserApiService';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { SystemConfiguration } from '../../../../types/superuser/SystemConfiguration';
import { useApiValidation } from '../../../../utils/useApiValidation';
import { emailConfigValidationSchema } from '../../../EmailConfigurationPage/EmailConfigurationPage.consts';
import { getInitialValues } from '../../../SystemConfigurationPage/SystemConfigurationPage.consts';
import { SystemConfigurationFormValues } from '../../../SystemConfigurationPage/SystemConfigurationPage.types';
import { useSetupPageStyles } from '../../SetupPage.styles';
import { EmailConfigurationForm } from './components/EmailConfigurationForm';
import { EmailTestForm } from './components/EmailTestForm';
import { getData } from './EmailConfigurationStep.consts';

type EmailConfigurationStepProps = {
  onEmailConfigurationCreate: () => void;
};

export const EmailConfigurationStep = memo<EmailConfigurationStepProps>(props => {
  const { t } = useTranslation(Namespaces.Setup);
  const classes = useSetupPageStyles();
  const { hasFieldApiError, getFieldApiError, resetApiError, setApiError } = useApiValidation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [errorModalState, setErrorModalState] = useState({
    isOpen: false,
    error: {},
  });
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isConfigurationLoading, setIsConfigurationLoading] = useState<boolean>(false);
  const [configurationData, setConfigurationData] = useState<SystemConfiguration | undefined>(undefined);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsConfigurationLoading(true);
        const config = await SuperuserUserApiService.fetchSystemConfiguration();
        setConfigurationData(config.data.payload);
      } catch (e) {
        const error = e as ApiClientError;
        if (error.response) {
          setApiError(error);
        }
      } finally {
        setIsConfigurationLoading(false);
      }
    };
    void fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialValues = useMemo(() => {
    if (configurationData) {
      return getInitialValues({
        ...configurationData,
        smtpHost: configurationData.smtpHost ?? '',
        smtpPort: `${configurationData.smtpPort ?? ''}`,
        smtpSecure: configurationData.smtpSecure ?? false,
        smtpUser: configurationData.smtpUser ?? '',
        smtpPass: configurationData.smtpPass ?? '',
        enquiryEmailLanguage: configurationData?.enquiryEmailLanguage || DefaultLanguage,
        smtpFromEmail: configurationData.smtpFromEmail ?? '',
        minCpuLimit: configurationData.minCpuLimit ?? undefined,
        maxCpuLimit: configurationData.maxCpuLimit ?? undefined,
        minMemoryLimit: configurationData.minMemoryLimit ?? null,
        maxMemoryLimit: configurationData.maxMemoryLimit ?? null,
        minStorageLimit: configurationData.minStorageLimit ?? null,
        maxStorageLimit: configurationData.maxStorageLimit ?? null,
        emailAddress: configurationData.emailAddress ?? '',
        emailToken: configurationData.emailToken ?? '',
      });
    }
    return getInitialValues();
  }, [configurationData]);

  const submit = async (values: SystemConfigurationFormValues) => {
    resetApiError();
    setIsFetching(true);
    try {
      const data = getData(values);
      await SuperuserUserApiService.updateSystemConfiguration({ data });
      setIsModalOpen(true);
      // props.onEmailConfigurationCreate();
    } catch (e) {
      const error = e as ApiClientError;
      if (error.response) {
        const jsonObject = JSON.stringify(error.response.data, null, 2);
        setApiError(error);
        setErrorModalState({
          isOpen: true,
          error: jsonObject,
        });
      }
      console.log(error);
    } finally {
      setIsFetching(false);
    }
  };

  const skip = () => {
    props.onEmailConfigurationCreate();
  };

  const cancel = () => {
    setIsModalOpen(false);
    props.onEmailConfigurationCreate();
  };

  const confirm = () => {
    setIsModalOpen(false);
    setIsTestModalOpen(true);
  };

  const testEmailSend = () => {
    setIsTestModalOpen(false);
    props.onEmailConfigurationCreate();
  };

  return isConfigurationLoading ? (
    <AlignCenter>
      <CircularProgress color="primary" />
    </AlignCenter>
  ) : (
    <Grid container className={classes.root}>
      <Grid item xs={6} className={classes.description}>
        <Typography data-cy="setup-email-configuration-form-title" variant="h1">
          {t('page.email_configuration.title')}
        </Typography>
        <Typography variant="body1">{t('page.email_configuration.description')}</Typography>
      </Grid>
      <Grid item xs={6} className={classes.formWrapper}>
        <Formik<SystemConfigurationFormValues>
          onSubmit={submit}
          initialValues={initialValues}
          validationSchema={emailConfigValidationSchema}
          validateOnChange
          validateOnBlur
        >
          {formProps => (
            <EmailConfigurationForm
              {...formProps}
              isFetching={isFetching}
              hasFieldApiError={hasFieldApiError}
              getFieldApiError={getFieldApiError}
              onSkip={skip}
            />
          )}
        </Formik>
      </Grid>
      <HUModal2 open={isTestModalOpen} size="medium">
        <EmailTestForm onTestEmailSend={testEmailSend} />
      </HUModal2>
      <SimpleConfirmModal
        open={isModalOpen}
        onCancel={cancel}
        onConfirm={confirm}
        message="Do you want to send a test email?"
      />
      <SimpleNotifyModal
        open={errorModalState.isOpen}
        message={
          errorModalState.error ? (
            <pre className={classes.modalError}>{errorModalState.error.toString()}</pre>
          ) : (
            t(`${Namespaces.Common}:errors.unexpected_error`)
          )
        }
        onOk={() => setErrorModalState({ isOpen: false, error: '' })}
      />
    </Grid>
  );
});
